import React, { useState, useEffect, useCallback } from 'react';
import { View, Dimensions, Animated } from 'react-native';
import { useNetInfo } from '@react-native-community/netinfo';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Text from '../text/Text';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useOfflineQueue } from '../../offline';

const { width } = Dimensions.get('window');
const OFFLINE_NOTICE_MOUNT_DELAY_MS = 200;

const OfflineNotice = () => {
	const insets = useSafeAreaInsets();
	const styles = useThemedStyles(createStyles);
	const netInfo = useNetInfo();
	const { isOnline, isReady } = useOfflineQueue();
	const [mounted, setMounted] = useState(false);
	const [slideAnim] = useState(() => new Animated.Value(-150));
	const [pulseAnim] = useState(() => new Animated.Value(1));
	// Track measured height so the banner can hide fully regardless of safe area size.
	const [bannerHeight, setBannerHeight] = useState(0);

	const offlineByNetInfo = netInfo.isConnected === false || netInfo.isInternetReachable === false;
	const offlineByContext = (isReady && !isOnline) || (!isReady && netInfo.isConnected === null);
	const showOffline = mounted && (offlineByNetInfo || offlineByContext);

	useEffect(() => {
		const timer = setTimeout(() => setMounted(true), OFFLINE_NOTICE_MOUNT_DELAY_MS);
		return () => clearTimeout(timer);
	}, []);

	// Slide down animation when showing, slide up when hiding
	useEffect(() => {
		Animated.timing(slideAnim, {
			toValue: showOffline ? 0 : -Math.max(bannerHeight || insets.top + 80, 80),
			duration: 300,
			useNativeDriver: true,
		}).start();
	}, [showOffline, slideAnim, bannerHeight, insets.top]);

	const handleLayout = useCallback((event) => {
		const { height } = event.nativeEvent.layout || {};
		if (height && height !== bannerHeight) {
			setBannerHeight(height);
		}
	}, [bannerHeight]);

	// Pulse animation for the indicator dot
	useEffect(() => {
		if (!showOffline) {
			return;
		}

		const pulse = Animated.loop(Animated.sequence([
			Animated.timing(pulseAnim, {
				toValue: 1.2,
				duration: 1000,
				useNativeDriver: true,
			}),
			Animated.timing(pulseAnim, {
				toValue: 1,
				duration: 1000,
				useNativeDriver: true,
			}),
		]));

		pulse.start();

		return () => pulse.stop();
	}, [showOffline, pulseAnim]);

	if (!mounted) {
		return null;
	}

	return (
		<Animated.View
			pointerEvents="none"
			onLayout={handleLayout}
			style={[
				styles.wrapper,
				// Android draws edge to edge too, so clear the status bar and camera cutout there as well.
				{ paddingTop: insets.top },
				{ transform: [{ translateY: slideAnim }] },
			]}
		>
			<View style={styles.offlineContainer}>
				<View style={styles.contentRow}>
					<Animated.View style={[styles.indicator, { transform: [{ scale: pulseAnim }] }]} />
					<View style={styles.textContainer}>
						<Text style={styles.offlineTitle}>Offline</Text>
						<Text style={styles.offlineMessage}>No internet connection</Text>
					</View>
				</View>
			</View>
		</Animated.View>
	);
};

const createStyles = (theme) => ({
	wrapper: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		alignItems: 'center',
		width,
		zIndex: 999,
	},
	offlineContainer: {
		backgroundColor: theme.importantColor1,
		paddingHorizontal: 20,
		paddingVertical: 12,
		justifyContent: 'center',
		alignItems: 'center',
		borderBottomLeftRadius: 12,
		borderBottomRightRadius: 12,
		elevation: 8,
		shadowColor: '#000',
		shadowOpacity: 0.2,
		shadowRadius: 8,
		shadowOffset: { width: 0, height: 3 },
		minWidth: 200,
	},
	contentRow: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	indicator: {
		width: 10,
		height: 10,
		borderRadius: 5,
		backgroundColor: theme.labelText1,
		marginRight: 12,
		opacity: 0.9,
	},
	textContainer: {
		flexDirection: 'column',
	},
	offlineTitle: {
		color: theme.labelText1,
		fontWeight: '700',
		fontSize: 14,
		letterSpacing: 0.3,
	},
	offlineMessage: {
		color: theme.labelText1,
		fontSize: 12,
		marginTop: 2,
		opacity: 0.85,
	},
});

export default OfflineNotice;
